import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, once } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { DSData, RequestError, Service } from 'webapp-types';
import { ForecastComparisonPeriod, TimeframeType } from 'webapp/enums/enums';
import { calculateAndFormatChange, calculateChange, getIndicatorClass } from 'webapp/utils/number';

import didUpdate from 'webapp/modifiers/did-update';
import type { CompareDisplayData } from 'webapp/components/block/content/common/compares';
import MetricDetailsTopDatapointsDatapointCardComponent from 'webapp/components/metric-details/top-datapoints/datapoint-card';

import type { Forecast } from 'webapp/models-custom';
import type DataSelect from 'webapp/models-custom/data-select';
import type DsDataPointTopLevel from 'webapp/models-custom/ds-data/point/top';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

interface Args {
	forecast: Forecast;
	isLoading: boolean;
	isRefreshing: boolean;
}

export default class MetricDetailsTopDatapointsForecastPreviousPeriodComponent extends Component<Args> {
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('request') declare requestService: Service.Request;

	private paramHash = '';
	@tracked isLoading = true;
	@tracked dataSelect: DataSelect | null = null;

	private dataQueryXHR: JQuery.jqXHR<unknown> | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.recreateDataQuery();
	}

	get isDataLoading(): boolean {
		return this.isLoading || this.args.isLoading;
	}

	@cached
	get previousPeriodPoint(): DsDataPointTopLevel | undefined {
		// isWholeRange = true → outputs[0].points contains a single aggregated point
		return this.dataSelect?.outputs?.[0]?.points?.find((p) => p.hasValue);
	}

	get valuePoint(): DChartPrimaryPoint {
		return new DChartPrimaryPoint({ valueStr: this.previousPeriodPoint?.valueStr });
	}

	get label(): string {
		const prefix =
			this.args.forecast?.comparisonPeriod === ForecastComparisonPeriod.SAME_PERIOD_1Y_AGO
				? 'Same period 1 year ago'
				: 'Previous period';
		const periodName = this.previousPeriodName;
		return periodName ? `${prefix} (${periodName})` : prefix;
	}

	get previousPeriodName(): string | null {
		const forecast = this.args.forecast;
		if (!forecast || forecast.endDate || !forecast.timeframe) {
			return null;
		}

		const { type, year } = forecast.timeframe;

		if (forecast.comparisonPeriod === ForecastComparisonPeriod.SAME_PERIOD_1Y_AGO) {
			return forecast.getTimeframeLabel(-1);
		}

		// PREVIOUS_PERIOD: shift one period back
		switch (type) {
			case TimeframeType.Y:
				return `${year - 1}`;
			case TimeframeType.Q1:
				return `Q4 ${year - 1}`;
			case TimeframeType.Q2:
				return `Q1 ${year}`;
			case TimeframeType.Q3:
				return `Q2 ${year}`;
			case TimeframeType.Q4:
				return `Q3 ${year}`;
			case TimeframeType.H1:
				return `H2 ${year - 1}`;
			case TimeframeType.H2:
				return `H1 ${year}`;
			default:
				return null;
		}
	}

	get comparePoints(): CompareDisplayData[] | undefined {
		const previousPoint = this.previousPeriodPoint;
		const forecastDS = this.args.forecast?.forecastWholeRangeDataSelect;
		const forecastPoint = forecastDS?.outputs?.[0]?.points?.find((p) => p.hasValue);

		if (!previousPoint || !forecastPoint) {
			return undefined;
		}

		const metric = this.args.forecast?.metricSetup?.metric;
		if (!metric) {
			return undefined;
		}

		const changeFormat = metric.defaults.changeFormat;
		const changeFunction = metric.defaults.changeFunction;

		const change = calculateChange(previousPoint.value, forecastPoint.value, changeFunction);
		if (change === null) {
			return undefined;
		}

		const changeStr = calculateAndFormatChange(
			previousPoint.value,
			forecastPoint.value,
			changeFormat,
			changeFunction,
		);
		const indicatorClass = getIndicatorClass(change, !!metric.changeInvert);

		const forecast = this.args.forecast;
		const forecastPeriodName = forecast?.endDate
			? null
			: forecast?.timeframe
				? forecast.getTimeframeLabel()
				: null;
		const compareDateRange = forecast?.compareDataSelect?.limitations?.dateRange;
		const compareText = forecastPeriodName
			? `vs ${forecastPeriodName}`
			: compareDateRange
				? `vs ${compareDateRange.rangeTitleShort}`
				: 'vs forecast';

		return [{ changeStr, indicatorClass, compareText }];
	}

	private setData(data: { previewData: DSData.RootTop | null; paramHash: string }): void {
		if (data.paramHash === this.paramHash) {
			this.dataSelect?.setFakeData(data.previewData);
			this.isLoading = false;
		}
	}

	private requestDataQuery(dataSelect: DataSelect, paramHash: string): void {
		if (this.dataQueryXHR && this.dataQueryXHR.readyState !== 4) {
			this.dataQueryXHR.abort();
		}

		const promise = this.mmDataSelectsService.fetchData(dataSelect, true);

		promise
			.then((previewData: DSData.RootTop) => {
				this.setData({ previewData, paramHash });
			})
			.catch((errorData: RequestError) => {
				this.setData({ previewData: null, paramHash });
				this.requestService.handleGenericThrownError(errorData);
			});

		this.dataQueryXHR = (promise.jqPromise as JQuery.jqXHR) ?? null;
	}

	private computeParamHash(): string {
		return JSON.stringify({
			timeframe: this.args.forecast?.timeframe,
			endDate: this.args.forecast?.endDate,
			comparisonPeriod: this.args.forecast?.comparisonPeriod,
			metricSetupData: this.args.forecast?.metricSetup?.metricSetupData,
		});
	}

	private recreateDataQuery(): void {
		next(() => {
			next(() => {
				const sourceDs = this.args.forecast?.compareDataSelect;
				const dataSelectData = sourceDs?.toJSON(true);

				if (!dataSelectData) {
					this.paramHash = '';
					this.isLoading = false;
					return;
				}

				const newParamHash = this.computeParamHash();

				if (this.paramHash === newParamHash) {
					this.isLoading = false;
					return;
				}

				this.isLoading = true;
				this.dataSelect?.setFakeData(null);
				this.paramHash = newParamHash;

				dataSelectData.id = this.metricDetailsService.generateMetricInsightsUUID();
				dataSelectData.blockId = null;
				const granulation = dataSelectData.dateRange!.granulation!; // a data select always has a granulation
				granulation.isWholeRange = true;
				granulation.timeUnit = null;
				granulation.multiplier = null;
				delete dataSelectData.compares;

				next(() => {
					const dataSelectModel =
						this.mmDataSelectsService.createNewModel(dataSelectData);
					this.requestDataQuery(dataSelectModel, newParamHash);
					this.unloadDataSelect();
					this.dataSelect = dataSelectModel;
				});
			});
		});
	}

	@action
	refreshDataQuery(): void {
		if (this.computeParamHash() === this.paramHash) {
			return;
		}
		this.isLoading = true;
		once(this, this.recreateDataQuery);
	}

	private unloadDataSelect(): void {
		if (this.dataSelect) {
			this.dataSelect.model.unloadRecord();
		}
	}

	override willDestroy(): void {
		super.willDestroy();
		this.dataQueryXHR?.abort();
		this.unloadDataSelect();
	}

	<template>
		<div
			{{didUpdate
				this.refreshDataQuery
				@forecast.timeframe
				@forecast.endDate
				@forecast.comparisonPeriod
				@forecast.metricSetup
			}}
		>
			<MetricDetailsTopDatapointsDatapointCardComponent
				@label={{this.label}}
				@valuePoint={{this.valuePoint}}
				@comparePoints={{this.comparePoints}}
				@isLoading={{this.isDataLoading}}
			/>
		</div>
	</template>
}
